import React, { useState, useEffect } from 'react';
import {
  Typography,
  Table,
  Button,
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Spin,
  Space,
  message,
  Input,
} from 'antd';
import { DollarOutlined, UserOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { supabase } from '../supabase/client';
import { getCurrentDestination } from '../services/destinationsService';
import moment from 'moment';

function BillingAdmin() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { Title } = Typography;

  const [subscriptionData, setSubscriptionData] = useState({
    currentPlan: 'Disabled',
    status: 'Active',
    nextBilling: null,
    amount: '$499.00',
  });

  const [billingHistory, setBillingHistory] = useState([]);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);

  useEffect(() => {
    const fetchSubscriptionData = async () => {
      try {
        const city = await getCurrentDestination();

        // Check Stripe subscription status
        const {
          data: { active, activeSubscriptions },
          error,
        } = await supabase.functions.invoke('check-subscription-status', {
          body: { cityId: city.id },
        });

        if (error) throw error;

        if (active && activeSubscriptions?.length > 0) {
          const subscription = activeSubscriptions[0];
          setSubscriptionData({
            currentPlan: 'Pro',
            status: subscription.status,
            nextBilling: subscription.current_period_end,
            amount: '$499.00',
          });
          setSubscriptionDetails(subscription);

          // Set billing history from invoices
          if (subscription.invoices) {
            setBillingHistory(
              subscription.invoices.map(invoice => ({
                id: invoice.id,
                date: new Date(invoice.transactions.paid_at * 1000).toLocaleDateString(),
                description: 'Monthly Pro Subscription',
                amount: invoice.amount_due / 100,
                status: invoice.status,
                invoice_url: invoice.hosted_invoice_url,
                pdf_url: invoice.invoice_pdf,
              }))
            );
          }
        } else {
          setSubscriptionData({
            currentPlan: 'Disabled',
            status: 'Inactive',
            nextBilling: null,
            amount: '$499.00',
          });
        }
      } catch (error) {
        message.error('Failed to load subscription data');
      }
    };

    fetchSubscriptionData();
  }, []);

  const [promoCode, setPromoCode] = useState('');
  const [discountedPrice, setDiscountedPrice] = useState('$499.00');

  const checkPromoCode = async code => {
    if (!code) {
      setDiscountedPrice('$499.00');
      return;
    }

    try {
      const {
        data: { price },
        error,
      } = await supabase.functions.invoke('check-promo-code', {
        body: { promoCode: code },
      });

      if (error) throw error;
      setDiscountedPrice(price);
    } catch (error) {
      console.error('Failed to check promo code:', error);
      setDiscountedPrice('$499.00');
    }
  };

  const handleUpgrade = async () => {
    const city = await getCurrentDestination();

    try {
      const {
        data: { session_url },
        error,
      } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          priceId: 'price_1QkToXACGyibZhfHd3GH17sP',
          promoCode: promoCode,
          cityId: city.id, // This should be dynamically set based on your app's context
        },
      });

      if (error) throw error;

      window.location.href = session_url;
    } catch (error) {
      message.error('Failed to start upgrade process');
    }
  };

  const handleUpdatePayment = async () => {
    window.location.href = 'https://billing.stripe.com/p/login/4gw5lpcaobU6a8odQQ';
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: amount => `$${amount.toFixed(2)}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
  ];

  if (loading)
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  if (error) return <Alert message={error} type="error" />;

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Billing & Subscription</Title>

      <Card title="Current Subscription" style={{ marginBottom: 24 }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={8}>
            <Statistic
              title="Plan"
              value={subscriptionData.currentPlan}
              prefix={<DollarOutlined />}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic title="Status" value={subscriptionData.status} prefix={<UserOutlined />} />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Next Billing Date"
              value={moment(subscriptionData.nextBilling).format('MM/D/YY')}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
        </Row>
        <Space style={{ marginTop: 24 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <Button
                type="primary"
                onClick={handleUpgrade}
                disabled={subscriptionData.currentPlan === 'Pro'}
              >
                Subscribe - $499/month
              </Button>
              <Button onClick={handleUpdatePayment}>Manage Subscription</Button>
            </Space>
          </Space>
        </Space>
      </Card>

      <Card title="Billing History">
        <Table
          columns={[
            ...columns,
            {
              title: 'Actions',
              key: 'actions',
              render: (_, record) => (
                <Space>
                  {record.invoice_url && (
                    <Button type="link" href={record.invoice_url} target="_blank">
                      View Invoice
                    </Button>
                  )}
                </Space>
              ),
            },
          ]}
          dataSource={billingHistory}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </div>
  );
}

export default BillingAdmin;
